import Layout from "@/components/elements/Layout";
import Image from "next/legacy/image";
import Link from "next/link";

import Schema from "@/components/schema/Schema";
import { getSEOByPathname } from "@/services/api/SEO";
import { ArrowSquareOut, MouseSimple } from "@phosphor-icons/react/dist/ssr";
import { createRef } from "react";
import ImageLogo from "../assets/images/nav-logo-vertical.svg";

export default function About({ seoData }) {
  const contentRef = createRef();
  const scrollToContent = () => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const structuredData = {
    "@context": "https://schema.org/",
    "@type": "Organization",
    "@id": "https://lucidityinsights.com/about/#header",
    name: "Lucidity Insights",
    alternateName: "lucidityinsights.com",
    description: "about description",
    url: "https://lucidityinsights.com/",
    sameAs: [
      "twitter.com/lucidityinsight",
      "instagram.com/lucidityinsights",
      "https://www.linkedin.com/company/lucidity-insights/",
    ],
    logo: {
      "@type": "ImageObject",
      "@id": "https://lucidityinsights.com/#image-logo",
      url: ImageLogo.src,
    },
  };

  const externalCoverage = [
    {
      date: "2025",
      type: "article",
      title:
        "The Startup & Investor Matchmaker: How Lucidity Insights is Reinventing Deal Flow in the Middle East and Africa",
      description:
        "TechBullion explores how Lucidity Insights is transforming the startup funding landscape in emerging markets.",
      url: "https://techbullion.com/the-startup-investor-matchmaker-how-lucidity-insights-is-reinventing-deal-flow-in-the-middle-east-and-africa/",
      thumbnail:
        "https://techbullion.com/wp-content/uploads/2025/09/Investor-Matchmaker.jpg",
    },
    {
      date: "2025",
      type: "profile",
      title:
        "Lucidity Insights: The Data Powerhouse Fuelling the Middle East and Africa's Startup Boom",
      description:
        "Entrepreneur Middle East profiles our mission to democratize access to startup ecosystem data and insights.",
      url: "https://www.entrepreneur.com/en-ae/entrepreneurs/lucidity-insights-the-data-powerhouse-fuelling-the-middle/489612",
      thumbnail:
        "https://assets.entrepreneur.com/content/3x2/2000/1744012287-TheMiddleEastishometonearly500millionpeoplerepresentingabout59oftheglobalpopulationandisprojectedtogrowtoover700millionby2050Despiteeconomicdisparitiesseveral13.png?format=pjeg&auto=webp&crop=16:9&width=675&height=380",
    },
    {
      date: "March 2025",
      type: "report launch",
      title:
        "New Lucidity Insights Report Highlights the Middle East as a New Global Fintech Contender",
      description:
        "Our latest special report in partnership with SAP explores the Middle East's emerging fintech landscape.",
      url: "https://www.entrepreneur.com/en-ae/growth-strategies/new-lucidity-insights-report-highlights-the-middle-east-as/488573",
      thumbnail:
        "https://assets.entrepreneur.com/content/3x2/2000/1742199605-Untitleddesign-2025-03-17T121952065.png?format=pjeg&auto=webp&crop=16:9&width=675&height=380",
    },
    {
      date: "July 2024",
      type: "report launch",
      title:
        "A Booming Market: A Look Into Saudi Arabia's Rapidly Evolving Food Delivery Landscape",
      description:
        "Special report examining Saudi Arabia's $10 billion food delivery market and growth projections.",
      url: "https://www.entrepreneur.com/en-ae/growth-strategies/a-new-special-report-by-lucidity-insights-in-partnership/477463",
      thumbnail:
        "https://assets.entrepreneur.com/content/3x2/2000/1721754459-hero.jpg?format=pjeg&auto=webp&crop=16:9&width=675&height=380",
    },
    {
      date: "June 2024",
      type: "report launch",
      title: "Investing in Egypt's Startup Ecosystem",
      description:
        "Comprehensive analysis of Egypt's startup and investor ecosystem, covering funding trends and challenges.",
      url: "https://www.entrepreneur.com/en-ae/starting-a-business/a-new-report-by-lucidity-insights-in-partnership-with/475955",
      thumbnail:
        "https://assets.entrepreneur.com/content/3x2/2000/1719169749-Untitleddesign-2024-06-23T230748057-min.png?format=pjeg&auto=webp&crop=16:9&width=675&height=380",
    },
    {
      date: "January 2024",
      type: "report launch",
      title: "The Business of Healthtech in MENA",
      description:
        "Deep dive into the $244 billion healthtech industry across the Middle East and North Africa region.",
      url: "https://www.entrepreneur.com/en-ae/news-and-trends/entrepreneur-middle-east-and-lucidity-insights-launch-new/468008",
      thumbnail:
        "https://assets.entrepreneur.com/content/3x2/2000/1704712382-Untitleddesign82.png?format=pjeg&auto=webp&crop=16:9&width=675&height=380",
    },
    {
      date: "December 2023",
      type: "report launch",
      title: "The Business of Impact Investing",
      description:
        "Partnership with Arowana Impact Capital exploring the growing trend of impact investing in emerging markets.",
      url: "https://www.entrepreneur.com/en-ae/starting-a-business/a-new-report-by-arowana-capital-built-in-partnership-with/466796",
      thumbnail:
        "https://assets.entrepreneur.com/content/3x2/2000/1706593675-Shutterstock-2164520031.jpg?format=pjeg&auto=webp&crop=16:9&width=675&height=380",
    },
    {
      date: "April 2023",
      type: "report launch",
      title:
        "How Saudi Arabia's Vision 2030 Has Spurred Entrepreneurship in the Kingdom",
      description:
        "Analysis of Saudi Arabia's startup ecosystem evolution from 2010-2022 and Vision 2030's impact.",
      url: "https://www.entrepreneur.com/en-ae/starting-a-business/a-look-at-how-saudi-arabias-vision-2030-has-spurred/449440",
      thumbnail:
        "https://assets.entrepreneur.com/content/3x2/2000/1681212213-Shutterstock-1490705324.jpg?format=pjeg&auto=webp&crop=16:9&width=675&height=380",
    },
    {
      date: "2024",
      type: "mention",
      title: "UAE tech start-ups to enjoy better funding prospects in 2024",
      description:
        "Khaleej Times coverage mentioning Lucidity Insights' market analysis and insights.",
      url: "https://www.khaleejtimes.com/business/uae-tech-start-ups-to-enjoy-better-funding-prospects-in-2024?_refresh=true",
      thumbnail:
        "https://imgengine.khaleejtimes.com/khaleejtimes/import/images/3b829d53-7df1-4769-9fff-2fe411334bbc-org.jpg?width=1200&height=800&format=auto",
    },
  ];

  return (
    <div>
      <Schema data={structuredData} />
      <Layout
        seoData={seoData}
        title={"About Lucidity Insights"}
        description={
          "Lucidity Insights: We specialize in investigative Special Reports. Our team of researchers, data experts, and storytellers provide informative and engaging insights."
        }
      >
        <div className='relative w-full min-h-screen overflow-x-hidden'>
          {/* Background Logo */}
          <div className='absolute top-0 right-0 h-[40vw] -translate-y-[10vw] aspect-[851/986] pointer-events-none'>
            <Image
              src='/images/lucidity.svg'
              alt='lucidity logo'
              layout='fill'
              objectFit='cover'
            />
          </div>
          {/* Header Section */}
          <div className='container py-10 lg:py-24'>
            <div className='relative z-10 max-w-3xl'>
              <h1 className='text-4xl font-semibold leading-none lg:tracking-tight lg:text-6xl font-display'>
                Where Data Meets the Future of Entrepreneurship
              </h1>

              <div className='max-w-2xl mt-5 space-y-4 leading-relaxed lg:space-y-6 lg:mt-8 text-white/70'>
                <p>
                  <span className='font-semibold text-transparent bg-gradient-to-r bg-clip-text from-orange-1 to-red-1'>
                    Lucidity Insights
                  </span>{" "}
                  is a business data & insights streaming platform, specialized
                  in developing world-class investigative special reports,
                  podcasts, and digital content covering the Startup Ecosystems
                  of the Middle East and Africa. Our digital content is told
                  across various multimedia channels, to provide maximum
                  exposure to the next generation of startup founders &
                  entrepreneurs.
                </p>
                <p>
                  Our mission is to become the go-to-hub for techpreneurs in the
                  Middle East & Africa; and to democratize access to the data,
                  tools and resources these founders need in order to become the
                  next generation of successful techpreneurs in the region.
                </p>
              </div>

              {/* Get in Touch Button */}
              <div className='mt-5 lg:mt-8'>
                <Link
                  href={"/contact-us"}
                  className='font-medium w-full lg:w-auto justify-center inline-flex items-center px-7 py-3 text-sm rounded-lg !h-auto text-white bg-gradient-to-l from-orange-1 to-red-1'
                >
                  Get In Touch with Us
                </Link>
              </div>
            </div>

            <div className='justify-center hidden mt-16 lg:flex'>
              <button
                className='flex items-center gap-1.5 text-sm opacity-30 hover:opacity-70'
                onClick={scrollToContent}
              >
                External Coverage
                <MouseSimple className='text-lg' weight='bold' />
              </button>
            </div>
          </div>

          {/* External Coverage Section */}
          <div className='relative bg-white/[0.02]' ref={contentRef}>
            <div className='container py-10 lg:py-16'>
              <h2 className='mb-4 text-3xl font-semibold leading-none text-white lg:leading-tight lg:text-5xl font-display'>
                External Coverage
              </h2>
              <p className='mb-12 text-sm lg:text-base text-white/60'>
                Discover how media outlets and industry publications have
                covered our work and insights
              </p>

              <div className='grid lg:grid-cols-3 gap-x-8 gap-y-10 lg:gap-y-16'>
                {externalCoverage.map((item, index) => (
                  <Link
                    key={index}
                    href={item.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex flex-col group'
                  >
                    <div className='relative w-full overflow-hidden rounded-lg aspect-video'>
                      <Image
                        src={item.thumbnail}
                        alt='lucidity logo'
                        layout='fill'
                        objectFit='cover'
                      />
                      <span className='absolute p-1.5 text-sm text-black bg-white rounded top-1.5 right-1.5'>
                        <ArrowSquareOut />
                      </span>
                    </div>
                    <div className='flex flex-wrap items-center gap-3 mt-4'>
                      <span className='text-xs font-medium text-white'>
                        {item.date}
                      </span>
                      <span className='px-2.5 py-0.5 text-[10px] font-bold text-white uppercase rounded-full bg-gradient-to-r from-orange-1 to-red-1'>
                        {item.type}
                      </span>
                    </div>

                    <div className='mt-3'>
                      <h3 className='mb-3 text-2xl font-medium leading-tight tracking-tight text-white break-words transition-colors duration-200 font-display group-hover:text-orange-1'>
                        {item.title}
                      </h3>
                      <p className='text-sm leading-relaxed break-words text-white/60'>
                        {item.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Layout>
      <style jsx>{`
        .border-cta {
          border-top: 1px solid transparent;
          border-bottom: 1px solid transparent;
          border-image: linear-gradient(
            to right,
            rgba(255, 211, 56, 1),
            rgba(205, 79, 79, 1)
          );
          border-image-slice: 1;
        }
      `}</style>
    </div>
  );
}

export async function getServerSideProps() {
  let seoData = {};
  try {
    const seoResponse = await getSEOByPathname("about");
    seoData = seoResponse.data;
  } catch (error) {
    console.error("Error fetching SEO data:", error);
  }

  return {
    props: {
      seoData,
    },
  };
}
